import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'

import { contentApi } from '../../api/content-api'
import { contentKeys } from '../../api/query-keys'

export function useContentDocument(entityId: string | null, documentId: string | null) {
  return useQuery({
    queryKey: contentKeys.document(entityId ?? '', documentId ?? ''),
    queryFn: entityId === null || documentId === null
      ? skipToken
      : () => contentApi.getDocument(entityId, documentId),
  })
}

export function useCreateContentDocument(entityId: string) {
  return useMutation({
    mutationFn: (input: Parameters<typeof contentApi.createDocument>[1]) => contentApi.createDocument(entityId, input),
    meta: { invalidates: [contentKeys.root] },
  })
}

export function useUpdateContentDocument(entityId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, input }: { documentId: string; input: Parameters<typeof contentApi.updateDocument>[2] }) =>
      contentApi.updateDocument(entityId, documentId, input),
    onSuccess: (updated) => client.setQueryData(contentKeys.document(entityId, updated.contentItemId), updated),
    meta: { invalidates: [contentKeys.root] },
  })
}

export function usePublishContentDocument(entityId: string) {
  return useMutation({
    mutationFn: ({ documentId, revision }: { documentId: string; revision: number }) =>
      contentApi.publishDocument(entityId, documentId, revision),
    meta: { invalidates: [contentKeys.root] },
  })
}

export function useContentVersions(entityId: string, documentId: string) {
  return useQuery({
    queryKey: contentKeys.versions(entityId, documentId),
    queryFn: () => contentApi.listVersions(entityId, documentId),
  })
}

export function useContentVersion(entityId: string, documentId: string, versionId: string | null) {
  return useQuery({
    queryKey: contentKeys.version(entityId, documentId, versionId ?? ''),
    queryFn: versionId === null ? skipToken : () => contentApi.getVersion(entityId, documentId, versionId),
  })
}

export function useRestoreContentVersion(entityId: string, documentId: string) {
  return useMutation({
    mutationFn: ({ versionId, ...input }: { versionId: string; revision: number; overwriteDraft: boolean }) =>
      contentApi.restoreVersion(entityId, documentId, versionId, input),
    meta: { invalidates: [contentKeys.root] },
  })
}

export function useDiscardContentDraft(entityId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, revision }: { documentId: string; revision: number }) =>
      contentApi.discardDraft(entityId, documentId, revision),
    onSuccess: (document) => client.setQueryData(contentKeys.document(entityId, document.contentItemId), document),
    meta: { invalidates: [contentKeys.root] },
  })
}

export function isContentDraftExists(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'CONTENT_DRAFT_EXISTS'
}

export function isContentItemChanged(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'CONTENT_ITEM_CHANGED'
}

export function contentPublishErrorMessage(error: unknown): string {
  return apiErrorMessage(error, 'The document could not be published. Try again.', {
    codes: {
      CONTENT_MEDIA_NOT_FOUND: 'Some document media could not be found. Remove or replace it before publishing.',
      CONTENT_ITEM_CHANGED: 'This document changed since you reviewed it. Review the current version, then publish again.',
    },
  })
}
